"use client";

import { useRouter } from "next/navigation";
import { useEffect } from "react";

/**
 * Global authentication error handler hook
 *
 * This hook listens for authentication errors (401) and automatically
 * redirects the user to logout when detected.
 *
 * Usage:
 * - Add this hook to your root layout or a high-level component
 * - It will catch unhandled 401 errors and force logout
 * - Provides a safety net for authentication failures
 */
export function useAuthErrorHandler() {
  const router = useRouter();

  useEffect(() => {
    // Handle promise rejections (including fetch errors)
    const handleRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;

      // Check if it's an authentication error
      if (
        error?.code === 401 ||
        error?.statusCode === 401 ||
        error?.status === 401
      ) {
        console.error(
          "Authentication error detected in promise rejection, logging out..."
        );
        event.preventDefault();
        window.location.href = "/api/auth/logout";
      }
    };

    // Handle generic errors
    const handleError = (event: ErrorEvent) => {
      const error = event.error;

      if (
        error?.code === 401 ||
        error?.statusCode === 401 ||
        error?.status === 401
      ) {
        console.error("Authentication error detected, logging out...");
        event.preventDefault();
        window.location.href = "/api/auth/logout";
      }
    };

    // Add event listeners
    window.addEventListener("unhandledrejection", handleRejection);
    window.addEventListener("error", handleError);

    // Cleanup
    return () => {
      window.removeEventListener("unhandledrejection", handleRejection);
      window.removeEventListener("error", handleError);
    };
  }, [router]);
}
